import React, { useEffect, useRef, useCallback } from "react";
import { OrbitControls } from "@react-three/drei";
import * as three from "three";
import useTransitionStore from "../../state/transitions";
import useCustomizerStore from "../../state/customizer";
import { useThree, useFrame } from "@react-three/fiber";
import { useControls, folder } from "leva";
import { clearSelectedHotspot } from "../../util/hotspotData";

function Controls() {
  const controlsRef = useRef();
  const { camera, events, controls } = useThree();
  const OrbitModeActive = useTransitionStore((s) => s.OrbitModeActive);
  const setSelectedHotspot = useCustomizerStore((s) => s.setSelectedHotspot);
  const setShowHotspots = useTransitionStore((s) => s.setShowHotspots);
  const ShowHotspots = useTransitionStore((s) => s.ShowHotspots);
  const CameraSide = useTransitionStore((s) => s.CameraSide);
  const setCameraSide = useTransitionStore((s) => s.setCameraSide);
  const setShowScrollContainer = useTransitionStore(
    (s) => s.setShowScrollContainer
  );
  const ResetAutoRotateDelta = useTransitionStore(
    (s) => s.ResetAutoRotateDelta
  );
  const HotspotControls = useTransitionStore((s) => s.HotspotControls);
  const setHotspotControls = useTransitionStore((s) => s.setHotspotControls);
  const AzimuthAndPolarAngles = useTransitionStore(
    (s) => s.AzimuthAndPolarAngles
  );
  const AzimuthAndPolarAnglesNeedUpdate = useTransitionStore(
    (s) => s.AzimuthAndPolarAnglesNeedUpdate
  );
  const setAzimuthAndPolarAnglesNeedUpdate = useTransitionStore(
    (s) => s.setAzimuthAndPolarAnglesNeedUpdate
  );
  const TempAzimuthAndPolarAngles = useTransitionStore(
    (s) => s.TempAzimuthAndPolarAngles
  );
  const setAzimuthAndPolarAngles = useTransitionStore(
    (s) => s.setAzimuthAndPolarAngles
  );
  const AutoRotate = useTransitionStore((s) => s.AutoRotate);
  const setAutoRotate = useTransitionStore((s) => s.setAutoRotate);
  const SuspensionMode = useTransitionStore((s) => s.SuspensionMode);
  const DisplayMode = useTransitionStore((s) => s.DisplayMode);
  const setSelectedHotspotDescription = useCustomizerStore(
    (s) => s.setSelectedHotspotDescription
  );
  const selectedHotspotDescription = useCustomizerStore(
    (s) => s.selectedHotspotDescription
  );
  const setSelectedHotspotTitle = useCustomizerStore(
    (s) => s.setSelectedHotspotTitle
  );
  const setFutureSelectedHotspotTitle = useCustomizerStore(
    (s) => s.setFutureSelectedHotspotTitle
  );
  const setFutureSelectedHotspotDescription = useCustomizerStore(
    (s) => s.setFutureSelectedHotspotDescription
  );
  const setSelectedDisclaimer = useCustomizerStore(
    (s) => s.setSelectedDisclaimer
  );
  const setDisclaimerOpen = useCustomizerStore((s) => s.setDisclaimerOpen);
  const setfutureSelectedDisclaimer = useCustomizerStore(
    (s) => s.setfutureSelectedDisclaimer
  );

  const { target, autoRotate } = useControls({
    CameraTarget: folder({
      target: {
        value: { x: 0, y: 0, z: 0 },
        onChange: (value) => {
          controlsRef.current.target.set(value.x, value.y, value.z);
          controlsRef.current.update();
        },
      },
      // autoRotate: {
      //   value: false,
      // },
    }),
  });

  const handleDisplayModeChange = useCallback(() => {
    setAutoRotate(false);
    controlsRef.current.autoRotate = false;
    Delta.current = 0;
    controlsRef.current.update();
  });

  const handleHotspotControlsChange = useCallback(() => {
    if (HotspotControls == true) {
      controlsRef.current.enabled = false;
      setAutoRotate(false);
      controlsRef.current.autoRotate = false;
      Delta.current = 0;
      controlsRef.current.update();
    } else {
      controlsRef.current.enabled = true;
      controlsRef.current.update();
    }
  });

  const handleAutoRotateChange = useCallback(() => {
    if (!autoRotate) controlsRef.current.autoRotate = false;
    else {
      controlsRef.current.autoRotate = true;
      controlsRef.current.autoRotateSpeed = 0.6;
    }
    controlsRef.current.update();
  });

  const handleOnStart = useCallback(() => {
    setAutoRotate(false);
    controlsRef.current.autoRotate = false;
    controlsRef.current.update();
    setShowHotspots(false);
    if (HotspotControls) return;
    setSelectedHotspotTitle("");
    setSelectedHotspotDescription("");
    setfutureSelectedDisclaimer("");
    setSelectedHotspot("");
    setFutureSelectedHotspotDescription("");
    setFutureSelectedHotspotTitle("");
    setSelectedDisclaimer("");
    setDisclaimerOpen(false);
    clearSelectedHotspot();
    setfutureSelectedDisclaimer("");
    setShowScrollContainer(false);
  });

  const handleOnChange = useCallback(() => {
    if (AzimuthAndPolarAnglesNeedUpdate && !SuspensionMode) {
      setAzimuthAndPolarAngles(TempAzimuthAndPolarAngles);
      setAzimuthAndPolarAnglesNeedUpdate(false);
    }
    if (Math.abs(camera.rotation.z) > (Math.PI * 2) / 4) {
      if (CameraSide == "driver") return;
      setCameraSide("driver");
    } else {
      if (CameraSide == "passenger") return;
      setCameraSide("passenger");
    }
  });

  useEffect(() => {
    events.disconnect();
  }, []);

  useEffect(() => {
    setHotspotControls(false);
    setDisclaimerOpen(false);
    setfutureSelectedDisclaimer("");
    setSelectedDisclaimer("");
  }, [DisplayMode]);

  useEffect(() => {
    handleAutoRotateChange();
  }, [autoRotate]);

  useEffect(() => {
    handleDisplayModeChange();
  }, [DisplayMode]);

  useEffect(() => {
    Delta.current = 0;
  }, [ResetAutoRotateDelta]);

  useEffect(() => {
    handleHotspotControlsChange();
  }, [HotspotControls]);

  // const AutoRotate = useRef(false);
  const Delta = useRef(0);
  const height = 3; //3
  const base = 0.95; //1
  const speed = 0.35;
  const delay = 1000; // 1000

  useFrame(({ camera, controls, clock }) => {
    // console.log(`controls`, camera.position);
    // console.log(`animuthal`, controls.getAzimuthalAngle());
    // console.log(`polar`, controls.getPolarAngle());
    if (
      DisplayMode === "flipper" ||
      HotspotControls ||
      SuspensionMode ||
      selectedHotspotDescription.length > 0
    ) {
      controlsRef.current.autoRotateSpeed = 0;
      controlsRef.current.autoRotate = false;
      return;
    }

    // else if (
    //   DisplayMode === "flipper" &&
    //   controlsRef.current.autoRotate === true
    // ) {
    //   controlsRef.current.autoRotateSpeed = 0;
    //   controlsRef.current.autoRotate = false;
    // }

    Delta.current = Delta.current + 1;
    if (Delta.current === delay && AutoRotate === false) {
      setAutoRotate(true);
      controlsRef.current.autoRotateSpeed = 0;
      controlsRef.current.autoRotate = true;
      controlsRef.current.update();
      setShowHotspots(false);
      setSelectedHotspotDescription("");
      setFutureSelectedHotspotTitle("");
      setFutureSelectedHotspotDescription("");
      setfutureSelectedDisclaimer("");
      setSelectedDisclaimer("");
      setDisclaimerOpen(false);
    }
    if (!AutoRotate) return;
    if (controlsRef.current.autoRotateSpeed < 0.6)
      controlsRef.current.autoRotateSpeed =
        controlsRef.current.autoRotateSpeed + 0.001;
    camera.position.setY(
      ((base + Math.sin(clock.elapsedTime * speed)) / 2) * height
    );
    camera.lookAt(0, 0, 0);
  });

  return (
    <>
      <OrbitControls
        enabled={DisplayMode !== "flipper"}
        ref={controlsRef}
        onStart={(e) => {
          handleOnStart();
        }}
        onEnd={() => {
          // console.log("end");
          setShowHotspots(true);
          Delta.current = 0;
        }}
        onChange={() => {
          // console.log("change");
          Delta.current = 0;
          handleOnChange();
        }}
        makeDefault
        // minDistance={HotspotControls ? 1 : 4} //4 //! this causes camera to jump if camera movement isn't in range of max and min. needs to be set after camera transition
        // maxDistance={20} //! over 10 causes jump in fov but should be 10 desktop
        enableDamping
        rotateSpeed={3}
        enableRotate={OrbitModeActive && !SuspensionMode}
        enablePan={false}
        // enableZoom={DisplayMode == "flipper" || !HotspotControls ? false : true}
        enableZoom={false}
        // enableZoom={true}
        minAzimuthAngle={AzimuthAndPolarAngles.minAzimuthAngle}
        maxAzimuthAngle={AzimuthAndPolarAngles.maxAzimuthAngle}
        minPolarAngle={AzimuthAndPolarAngles.minPolarAngle}
        maxPolarAngle={AzimuthAndPolarAngles.maxPolarAngle}
      />
    </>
  );
}
export default React.memo(Controls);
